/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  accessAPI,
  Boton,
  Formulario,
  Sidebar,
  TablaFiltrable,
  Titulo
} from "../../../shared";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";

const FILTROS = [];
const SEARCH_FIELDS = ["numero", "productoEtiqueta", "cajaNumero"];

export default function MuestrasCliente({ clienteId }) {
  const [loader, setLoader] = useState(true);
  const [muestras, setMuestras] = useState([]);
  const [sidebarNueva, setSidebarNueva] = useState(false);
  const [loaderNueva, setLoaderNueva] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!clienteId) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  function cargar() {
    setLoader(true);
    accessAPI(
      "GET",
      `admin/muestra/cliente/${clienteId}`,
      null,
      (resp) => {
        setMuestras(resp);
        setLoader(false);
      },
      (resp) => {
        setLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp?.[0]?.msg || "Error al cargar muestras",
          sticky: true,
        });
      },
    );
  }

  function ingresarMuestra(campos) {
    const numero = parseInt(campos.numero);
    const productoid = campos.productoid ? parseInt(campos.productoid) : null;
    const actividadid = campos.actividadid ? parseInt(campos.actividadid) : null;
    if (!numero) return;

    setLoaderNueva(true);
    accessAPI(
      "GET",
      `admin/muestra/numero/${numero}`,
      null,
      (muestra) => {
        if (muestra.estado !== "PENDIENTE") {
          setLoaderNueva(false);
          const clienteDueño = muestra.producto?.proceso?.clienteid;
          // La muestra ya está ingresada, pero en el cliente de OTRO — no abrir
          // su ficha por error, bloquear acá con un mensaje claro.
          if (clienteDueño && String(clienteDueño) !== String(clienteId)) {
            toast.current?.show({
              severity: "error",
              summary: "Muestra de otro cliente",
              detail: `La muestra #${numero} ya está registrada en el cliente "${
                muestra.producto?.proceso?.cliente?.razonsocial || "otro"
              }". Verificá el número.`,
              sticky: true,
            });
            return;
          }
          toast.current?.show({
            severity: "warn",
            summary: "Muestra ya ingresada",
            detail: `La muestra #${numero} ya fue ingresada previamente; se abre la ficha`,
          });
          navegarADetalle(muestra.id);
          return;
        }
        accessAPI(
          "PUT",
          `admin/muestra/${muestra.id}`,
          { productoid, actividadid },
          () => {
            setLoaderNueva(false);
            setSidebarNueva(false);
            navegarADetalle(muestra.id);
          },
          (resp) => {
            setLoaderNueva(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp?.[0]?.msg || "Error al ingresar la muestra",
              sticky: true,
            });
          },
        );
      },
      (resp) => {
        setLoaderNueva(false);
        toast.current?.show({
          severity: "error",
          summary: "Muestra no encontrada",
          detail: resp?.[0]?.msg || `No existe una muestra con número ${numero}`,
          sticky: true,
        });
      },
    );
  }

  function navegarADetalle(id) {
    const volver = encodeURIComponent(`/clientes/${clienteId}/procesos`);
    navigate(`/muestra/${id}?volver=${volver}`);
  }

  const ingresarConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "numero",
            tipoitemformularioid: 4,
            etiqueta: "Número",
            requerido: true,
          },
          {
            nombre: "procesoid",
            tipoitemformularioid: 5,
            etiqueta: "Proceso",
            opciones: `admin/muestra/procesos?clienteid=${clienteId}`,
            requerido: true,
            sinOpcionInicial: true,
          },
          {
            nombre: "productoid",
            tipoitemformularioid: 5,
            etiqueta: "Producto (opcional)",
            opciones: "admin/muestra/productos",
            queryparams: ["procesoid"],
            sinOpcionInicial: true,
          },
          {
            nombre: "actividadid",
            tipoitemformularioid: 5,
            etiqueta: "Actividad (opcional)",
            opciones: "admin/muestra/actividades",
            queryparams: ["procesoid"],
            sinOpcionInicial: true,
          },
        ],
      },
    ],
    botones: [
      { etiqueta: "Ingresar muestra", callback: ingresarMuestra, validar: true },
    ],
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <div
        css={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "0.5em",
        }}
      >
        <Boton
          callback={() => {
            setFormKey((k) => k + 1);
            setSidebarNueva(true);
          }}
          texto
          icono
          chico
          negro
        >
          <i className="pi pi-plus"></i>
        </Boton>
      </div>

      {loader ? (
        <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} width="100%" height="3rem" />
          ))}
        </div>
      ) : (
        <TablaFiltrable
          value={muestras}
          filtros={FILTROS}
          searchFields={SEARCH_FIELDS}
          nombreArchivo="muestras_cliente"
          emptyMessage="Este cliente no tiene muestras"
          rows={10}
          onRowClick={(e) => {
            const volver = encodeURIComponent(
              `/clientes/${clienteId}/procesos`,
            );
            navigate(`/muestra/${e.data.id}?volver=${volver}`);
          }}
          css={{ "& .p-datatable-tbody > tr": { cursor: "pointer" } }}
        >
          <Column
            header="N°"
            body={(m) => (
              <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span>{m.numero}</span>
                {m.desactivada ? (
                  <Tag value="Desactivada" severity="danger" />
                ) : m.retirada ? (
                  <Tag value="Retirada" severity="warning" />
                ) : null}
              </div>
            )}
          />
          <Column header="Producto" field="productoEtiqueta" />
          <Column header="Caja" body={(m) => m.cajaNumero ?? "—"} />
        </TablaFiltrable>
      )}

      <Sidebar
        visible={sidebarNueva}
        onHide={() => setSidebarNueva(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Ingresar nueva muestra</Titulo>
        <Formulario
          key={formKey}
          config={ingresarConfig}
          loader={loaderNueva}
        />
      </Sidebar>
    </>
  );
}
